import React from 'react';
import {observer} from 'mobx-react';
import {SingleGameComponent} from '../single-game/single-game.component';
import {SingleGamePlaceholderComponent} from '../single-game-placeholder/single-game-placeholder.component';
import classNames from 'classnames';
import {SwiperWrapperBox} from './boxes/swiper-wrapper/swiper-wrapper.box';
import {SwiperGamesListBox} from './boxes/swiper-games-list/swiper-games-list.box';
import {SwiperBox} from './boxes/swiper/swiper.box';
import {SingleGameSkeletonComponent} from '../single-game/single-game-skeleton.component';
import {CyClass} from '../../../common/cy-classes';
import {TextTranslation} from "../../services/translation/text-translation";
import {OrbitComponent} from "../base-components/orbit-component/orbit-component";
import {ISwiperListViewModel} from "../../services/arenas/models/lists/swiper-list-view-model.interface";
import { observable, action, makeObservable } from 'mobx';

export interface OrbitSwiperComponentProps {
    viewModel: ISwiperListViewModel;
    numberOfPlaceholders?: number;
    skipIntermediatePage?: boolean;
    showNumberBadge?: boolean;
    className?: string;
    listPosition: number;
    lastGameType?: number;
    onGameBringInCentralView?: (gameIndex: number) => void;
}


@observer
export class OrbitSwiperComponent extends OrbitComponent<OrbitSwiperComponentProps> {
    constructor(props: OrbitSwiperComponentProps) {
        super(props);

        makeObservable(this);
    }

    private get _title(): TextTranslation {
        return this.props.viewModel.title;
    }

    private get _shouldUseSquareIcons(): boolean {
        return this.props.viewModel.shouldUseSquareIcons();
    }

    private _renderOneGame(gameType: number, gameIndex: number, isLastGametype?: boolean) {
        return <SingleGameComponent
            key={gameType}
            gameType={gameType}
            seoCategory={this.props.viewModel.arenaName}
            disableGameInfoHover={false}
            listTitle={this._title.translationKey}
            listPosition={this.props.listPosition}
            AdminListID={this.props.viewModel.listId}
            AIListTypeID={this.props.viewModel.listTypeId}
            skipIntermediatePage={this.props.skipIntermediatePage}
            templateType={this.props.viewModel.templateType}
            intersectionTarget={this._swiperWrapperElement}
            gameIndex={gameIndex}
            showNumberBadge={this.props.showNumberBadge}
            isInsideSwiper={true}
            useSquareIcons={this._shouldUseSquareIcons}
            aiEngine={this.aiEngine}
            hasTwoLinesTitle={this.props.viewModel.showGameTitleOnMultipleLines}
            isLastGametype={isLastGametype}
            onBringInCentralView={this.props.onGameBringInCentralView}
        />;
    }

    private _renderPlaceholders() {

        if (this.props.viewModel.numberOfGamesSkeletons > 0) {
            return null;
        }

        let placeHolders: React.ReactNode[] = [];
        let numberOfPlaceholders = this.props.numberOfPlaceholders || 0;

        for (let i = 0; i < numberOfPlaceholders; i++) {
            placeHolders.push(<SingleGamePlaceholderComponent key={'game_placeholder_' + i} isInsideSwiper={true}/>);
        }

        return placeHolders;
    }


    private _swiperGameListElement: HTMLDivElement | null = null;

    private _captureSwiperGameListElement = (element: HTMLDivElement | null) => {
        this._swiperGameListElement = element;
        this.props.viewModel.swipeLogicHandler.setElement(element);
    };

    /* I hate to put observable on a dom element but for now I cannot see another method to rapidly notify child nodes that the wrapper is ready to be used in IntersectionObserver as root
    I can create the intersection observer here and pass it instead of the DOM element but I would also need to put observable on the intersection observer */
    @observable.ref
    private _swiperWrapperElement: HTMLDivElement | null = null;
    @action.bound
    private _captureSwiperWrapperElement = (element: HTMLDivElement | null) => {
        this._swiperWrapperElement = element;
    };

    private get aiEngine(): string {
        return this.props.viewModel.aiEngine;
    }

    private _renderGamesSkeletons() {

        let numSkeletons = this.props.viewModel.numberOfGamesSkeletons;
        let placeholderImageSrc: string;
        if (this._shouldUseSquareIcons) {
            placeholderImageSrc = this.services.theme.currentTheme?.images.gameIconPlaceHolderSq.toString();
        } else {
            placeholderImageSrc = this.services.theme.currentTheme?.images.gameIconPlaceHolder.toString();
        }

        let skeletons = [];

        for (let i = 1; i <= numSkeletons; i++) {
            skeletons.push(
                <SingleGameSkeletonComponent
                    key={'skeleton_' + i}
                    templateType={this.props.viewModel.templateType}
                    useSquareIcons={this._shouldUseSquareIcons}
                    placeholderImageSrc={placeholderImageSrc}
                />
            );
        }

        return skeletons;
    }

    private _renderGames() {

        const gameTypes = this.props.viewModel.gameTypes;
        if (gameTypes.length > 0) {
            return gameTypes.map((gameType, index) => {
                return this._renderOneGame(gameType, index, gameType === this.props.lastGameType)
            });
        }

        return this._renderGamesSkeletons();
    }

    componentDidMount() {
        super.componentDidMount();

        this.reaction(() => [this.props.viewModel.gameTypes],
            () => this.props.viewModel.swipeLogicHandler.setElement(this._swiperGameListElement),
            {
                fireImmediately: true
            });
    }

    componentDidUpdate(prevProps: Readonly<OrbitSwiperComponentProps>, prevState: Readonly<{}>, snapshot?: any) {
        this.props.viewModel.swipeLogicHandler.setElement(this._swiperGameListElement)
    }

    render() {
        if (!this.props.viewModel.isVisible) {
            return null;
        }

        return (
            <SwiperBox className={classNames(CyClass('cy-orbit-swiper'), this.props.className)}>

                {this.props.viewModel.renderListHeader()}

                <SwiperWrapperBox className={classNames('cy-orbit-swiper-wrapper', this.props.viewModel.templateType)}
                                  ref={this._captureSwiperWrapperElement}>
                    <SwiperGamesListBox className='cy-orbit-swiper-games-list' ref={this._captureSwiperGameListElement}
                                        isTouchDevice={this.services.device.feature.isTouch}>
                        {this.props.viewModel.renderSpecialWidgets()}
                        {this._renderGames()}
                        {this._renderPlaceholders()}
                    </SwiperGamesListBox>
                    {this.props.viewModel.renderListNavigation()}
                </SwiperWrapperBox>

                {this.props.viewModel.renderListFooter()}

            </SwiperBox>
        );
    }
}
